import type { Restaurant } from "@/API";
import RestaurantCard from "@/components/RestaurantCard";
import { Button } from "@/components/ui/button";
import {
  deleteFavoriteList,
  resetDeleteFavoriteList,
  userFavoriteListState,
} from "@/store/userFavoriteListState";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { useCallback, useEffect, useState, type FC } from "react";
import { Link, useNavigate, useParams } from "react-router";
import Text from "@/components/Text";
import ConfirmationDialog from "@/components/dialogs/ConfirmationDialog";
import { historyRestaurantsState } from "@/store/historyRestaurantsState";
import useDetectSizeDevice from "@/hooks/useDetectSizeDevice";
import ConfirmationDrawer from "@/components/drawers/ConfirmationDrawer";
import { useTranslation } from "react-i18next";

const getCalculateCenter = (restaurants: Restaurant[]) => {
  const lat =
    restaurants
      .map((restaurant) => restaurant.position.latitude)
      .reduce((accumulator, currentValue) => accumulator + currentValue) /
    restaurants.length;
  const lng =
    restaurants
      .map((restaurant) => restaurant.position.longitude)
      .reduce((accumulator, currentValue) => accumulator + currentValue) /
    restaurants.length;

  return {
    lat,
    lng,
  };
};

const Favorite: FC = () => {
  const { t } = useTranslation();
  const { favoriteId } = useParams<{ favoriteId?: string }>();
  const navigate = useNavigate();
  const { sm } = useDetectSizeDevice();
  const {
    data: favoriteListRestaurants,
    deletedFavoriteList,
    deletingFavoriteList,
  } = userFavoriteListState();
  const { data: historyRestaurants } = historyRestaurantsState();
  const { isLoaded } = useJsApiLoader({
    id: "55f6b0cbd2ae313dcf7f2bce",
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_KEY,
  });
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [dialogToDeleteFavoriteList, setDialogToDeleteFaovriteList] =
    useState(false);

  const onLoad = useCallback((map: google.maps.Map) => {
    setMap(map);
  }, []);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  useEffect(() => {
    if (map && sm && !deletedFavoriteList) {
      let restaurants: Restaurant[] = [];

      const favoriteRestaurants =
        favoriteListRestaurants &&
        favoriteId &&
        favoriteId !== "history" &&
        favoriteListRestaurants[favoriteId]
          ? favoriteListRestaurants[favoriteId]
          : null;

      if (favoriteRestaurants) {
        restaurants = favoriteRestaurants.restaurants;
      }

      if (
        !favoriteRestaurants &&
        historyRestaurants &&
        favoriteId === "history"
      ) {
        restaurants = historyRestaurants;
      }

      if (restaurants.length > 1) {
        const latitudeList = restaurants.map(
          (restaurant) => restaurant.position.latitude,
        );
        const longitudeList = restaurants.map(
          (restaurant) => restaurant.position.longitude,
        );

        const minLat = Math.min(...latitudeList);
        const maxLat = Math.max(...latitudeList);

        const minLng = Math.min(...longitudeList);
        const maxLng = Math.max(...longitudeList);

        const bounds = new google.maps.LatLngBounds(
          new google.maps.LatLng(maxLat, minLng),
          new google.maps.LatLng(minLat, maxLng),
        );

        map.fitBounds(bounds);
      }
    }
  }, [
    map,
    favoriteListRestaurants,
    historyRestaurants,
    favoriteId,
    sm,
    deletedFavoriteList,
  ]);

  useEffect(() => {
    if (deletedFavoriteList) {
      navigate("/favorites");
      setDialogToDeleteFaovriteList(false);
      resetDeleteFavoriteList();
    }
  }, [deletedFavoriteList]);

  const favoriteRestaurants =
    favoriteId !== "history" &&
    favoriteListRestaurants &&
    favoriteId &&
    favoriteListRestaurants[favoriteId]
      ? favoriteListRestaurants[favoriteId]
      : null;

  let restaurants: Restaurant[] = [];

  if (favoriteRestaurants) {
    restaurants = favoriteRestaurants.restaurants;
  }

  if (!favoriteRestaurants && historyRestaurants && favoriteId === "history") {
    restaurants = historyRestaurants;
  }

  if (!favoriteRestaurants && favoriteId !== "history") return null;

  const center =
    restaurants.length === 1
      ? {
          lat: restaurants[0].position.latitude,
          lng: restaurants[0].position.longitude,
        }
      : getCalculateCenter(restaurants);

  return (
    <>
      {favoriteRestaurants && sm && (
        <ConfirmationDialog
          title={t("delete_favorite_list_title")}
          description={t("delete_favorite_list_description", {
            favoriteListName: favoriteRestaurants.favorite.name,
          })}
          open={dialogToDeleteFavoriteList}
          onClose={() => {
            setDialogToDeleteFaovriteList(false);
          }}
          onConfirm={() => {
            deleteFavoriteList({
              favoriteListId: favoriteRestaurants.favorite.id,
            });
          }}
          cancelWording={t("cancel")}
          confirmWording={t("delete")}
          isDisabled={deletingFavoriteList}
        />
      )}
      {favoriteRestaurants && !sm && (
        <ConfirmationDrawer
          title={t("delete_favorite_list_title")}
          description={t("delete_favorite_list_description", {
            favoriteListName: favoriteRestaurants.favorite.name,
          })}
          open={dialogToDeleteFavoriteList}
          onClose={() => {
            setDialogToDeleteFaovriteList(false);
          }}
          onConfirm={() => {
            deleteFavoriteList({
              favoriteListId: favoriteRestaurants.favorite.id,
            });
          }}
          cancelWording={t("cancel")}
          confirmWording={t("delete")}
          isDisabled={deletingFavoriteList}
        />
      )}

      <div
        className="flex"
        style={{
          height: `calc(100dvh - var(--height-header))`,
        }}
      >
        <div className="w-[100%] sm:w-[70%] px-[16px] pt-[24px] pb-[16px] sm:p-[24px] overflow-y-auto">
          <div className="flex justify-between mb-[24px]">
            <Link to="/favorites">
              <Button variant="secondaryOutlined">{t("back")}</Button>
            </Link>
            {favoriteRestaurants && (
              <Button
                variant="secondaryOutlined"
                onClick={() => {
                  setDialogToDeleteFaovriteList(true);
                }}
              >
                {t("delete_favorite_list")}
              </Button>
            )}
          </div>
          <Text fontSize={20} fontWeight={600}>
            {favoriteRestaurants
              ? favoriteRestaurants.favorite.name
              : t("recently_viewed")}
          </Text>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-[16px] sm:gap-[24px] mt-[24px]">
            {restaurants.map((restaurant) => (
              <RestaurantCard
                image={restaurant?.imageUrl ?? undefined}
                name={restaurant.name}
                address={restaurant.address ?? "N/A"}
                rate={restaurant.rating ?? "0"}
                tags={
                  restaurant?.categories?.filter(
                    (it) => typeof it === "string",
                  ) ?? []
                }
                platform={restaurant.platform.toLowerCase()}
                krunshScore={restaurant.krunshScore}
                deliveryTime={restaurant.deliveryTime}
                canBeFavorited
                isFavorite
                onClick={() => {
                  navigate(`/explorer/restaurant/${restaurant.id}`);
                }}
              />
            ))}
          </div>
        </div>
        {sm && (
          <div className="p-[24px] w-[30%] bg-[white]">
            {isLoaded && (
              <GoogleMap
                center={center}
                zoom={16}
                mapContainerStyle={{
                  width: "100%",
                  borderRadius: 12,
                  height: `calc(100vh - var(--height-header) - 48px`,
                }}
                onLoad={onLoad}
                onUnmount={onUnmount}
                options={{
                  fullscreenControl: false,
                  mapTypeControl: false,
                }}
              >
                {restaurants.map((restaurant) => (
                  <Marker
                    key={restaurant.id}
                    position={{
                      lat: restaurant.position.latitude,
                      lng: restaurant.position.longitude,
                    }}
                    icon={`/img/pins/${restaurant.platform.toLowerCase()}.svg`}
                  />
                ))}
              </GoogleMap>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default Favorite;
